<template>
    <div>
        <x-data-table
            class="no-row-hover"
            :headers="mtaMainHeaders"
            :items="midTermAdjustmentFormatted"
            :expand="expand"
            hide-actions
            :loading="mtaLoading"
            loading-text="Loading... Please wait"
        >
            <template v-slot:no-data>
                <p class="error-message text-xs-center">
                    <span v-if="mtaLoading">Loading... Please wait</span>
                    <span v-else>No mid-term adjustment made</span>
                </p>
            </template>

            <template v-slot:items="props">
                <tr v-if="!(props.item.status === 'draft' && isInsurer)">
                    <td>
                        {{ props.item.effective_date | formatDate }}
                    </td>

                    <td>{{ props.item.reference }}</td>

                    <td>{{ props.item.title }}</td>

                    <td>{{ getTotalMTA(props.item) | currency(declaration.currency.code) }}</td>

                    <td>
                        {{ props.item.settlement_due_date | formatDate }}
                    </td>

                    <td>{{ props.item.premium_payment_term }} days</td>

                    <td>
                        <status-chip
                            class="ma-0"
                            small
                            :status="getStatus(props.item.status)"
                        />
                    </td>

                    <td class="text-xs-right">
                        <template v-if="showViewDocumentsModal(props.item)">
                            <x-dialog v-model="dialog[props.item.id]" width="900" max-width="900">
                                <template v-slot:activator="{ on }">
                                    <x-btn color="tertiary" small v-on="on">View documents</x-btn>
                                </template>

                                <x-container>
                                    <document-list
                                        v-if="props.item.id"
                                        :documentable-id="props.item.id"
                                        documentable-type="endorsement"
                                        :can-delete="() => false"
                                        :upload=false
                                    />
                                </x-container>
                            </x-dialog>
                        </template>

                        <x-btn
                            v-if="canCancel(props.item.status)"
                            class="square-icon"
                            color="tertiary"
                            small
                            :disabled="disabled"
                            @click="cancelMidTermAdjustment(props.item)"
                        >
                            <i class="material-icons">delete</i>
                        </x-btn>

                        <x-btn
                            v-if="canPreview(props.item)"
                            class="square-icon"
                            color="tertiary"
                            small
                            :loading="showMidTermAdjustmentDialog"
                            @click="reviewMidTermAdjustment"
                        >
                            <i class="material-icons">preview</i>
                        </x-btn>

                        <x-btn
                            v-if="canOpenMta(props.item)"
                            class="square-icon ml-2"
                            color="tertiary"
                            small
                            :loading="showMidTermAdjustmentDialog"
                            @click="goToMta(props.item.id)"
                        >
                            <i class="material-icons">preview</i>
                        </x-btn>

                        <x-btn
                            v-if="canEditMidTermAdjustment(props.item.status)"
                            class="square-icon ml-2"
                            color="tertiary"
                            small
                            :disabled="disabled || updatingEditingMTA"
                            @click="editMidTermAdjustment(props.item)"
                        >
                            <i class="material-icons">edit</i>
                        </x-btn>

                        <x-btn
                            v-if="canDownloadMTA(props.item)"
                            class="square-icon ml-2"
                            color="tertiary"
                            small
                            :disabled="disabled"
                            @click="downloadMTAMarketSheet(props.item)"
                        >
                            <i class="material-icons">file_download</i>
                        </x-btn>
                    </td>

                    <td class="text-xs-right">
                        <x-btn
                            class="square-icon ml-2"
                            color="tertiary"
                            small
                            @click="props.expanded = !props.expanded"
                        >
                            <i class="material-icons">
                                {{ props.expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                            </i>
                        </x-btn>
                    </td>
                </tr>
            </template>

            <template v-slot:expand="props">
                <x-data-table
                    class="no-row-hover pa-3"
                    :headers="mtaSubHeaders"
                    :items="adjustmentRows(props.item)"
                    item-key="row_key"
                    hide-actions
                >
                    <template v-slot:no-data>
                        <p class="error-message text-xs-center">
                            No mid-term adjustment made
                        </p>
                    </template>

                    <template v-slot:items="requests">
                        <tr v-if="!(requests.item.request.status === 'draft' && isInsurer)">
                            <td>
                                {{ getInsurerCompanyName(requests.item.request) }}

                                <div v-if="requests.item.line" class="underwriter-details">Reference:
                                    {{ requests.item.line.underwriter_reference }}
                                </div>

                                <div v-if="requests.item.line" class="underwriter-details">Risk code:
                                    {{ requests.item.line.risk_code }}
                                </div>

                                <div
                                    v-if="requests.item.line && requests.item.line.security_id"
                                    class="underwriter-details"
                                >
                                    Security: {{ requests.item.line.security.name }}
                                </div>
                            </td>

                            <td v-if="!isInsurer" class="text-xs-right">
                                <template v-if="requests.item.line">
                                    {{ requests.item.line.line_amount | currency(declaration.currency.code) }}
                                    <br>
                                    <x-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">
                                                {{ requests.item.line.line_percent_display | percent(4) }}
                                            </span>
                                        </template>

                                        <span>{{ requests.item.line.line_percent | percent(12) }}</span>
                                    </x-tooltip>
                                </template>
                            </td>

                            <td class="vertical-align-top">
                                <template v-if="requests.item.editable && isEditing(requests.item)">
                                    <x-text-field
                                        v-model="editingMTA.adjustment_amount"
                                        placeholder=" "
                                        :prefix="declaration.currency.code"
                                        :disabled="updatingEditingMTA"
                                        :rules="[
                                            (v) => ruleRequired(v, 'Adjustment amount'),
                                            (v) => ruleNumeric(v, 'Adjustment amount'),
                                        ]"
                                    />
                                </template>

                                <template v-else>
                                    {{ requests.item.adjustment_amount | currency(declaration.currency.code) }}
                                </template>
                            </td>

                            <td>
                                <template v-if="requests.item.editable && isEditing(requests.item)">
                                    <x-textarea
                                        v-model="editingMTA.commentary"
                                        placeholder=" "
                                        :disabled="updatingEditingMTA"
                                        :rules="[v => ruleMaxCharacters(v, 300, 'Commentary')]"
                                    />
                                </template>

                                <template v-else>
                                    {{ requests.item.commentary }}
                                </template>
                            </td>

                            <td :class="{ 'text-xs-right': requests.item.editable && isEditing(requests.item) }">
                                <template v-if="requests.item.editable && isEditing(requests.item)">
                                    <x-btn
                                        color="tertiary"
                                        small
                                        :disabled="updatingEditingMTA"
                                        :loading="updatingEditingMTA"
                                        @click="editingMTA = null"
                                    >
                                        Cancel
                                    </x-btn>
                                </template>

                                <template v-else>
                                    <status-chip
                                        small
                                        :status="getStatus(requests.item.request.status, requests.item.request.insurer_company_id)"
                                    />
                                </template>
                            </td>

                            <td v-if="requests.item.editable && canEditMidTermAdjustmentApproval(requests.item.request.status, props.item.status)" class="text-xs-right">
                                <template v-if="isEditing(requests.item)">
                                    <x-btn
                                        color="secondary"
                                        small
                                        :disabled="updatingEditingMTA"
                                        :loading="updatingEditingMTA"
                                        @click="updateInsurerMTA()"
                                    >
                                        Update
                                    </x-btn>
                                </template>

                                <template v-else>
                                    <x-btn
                                        class="square-icon"
                                        color="tertiary"
                                        small
                                        :disabled="disabled || updatingEditingMTA"
                                        @click="editInsurerMTA(requests.item)"
                                    >
                                        <i class="material-icons">edit</i>
                                    </x-btn>
                                </template>
                            </td>
                        </tr>
                    </template>
                </x-data-table>
            </template>
        </x-data-table>

        <modal
            ref="confirmMidTermAdjustmentCancel"
            title="Cancel mid-term adjustment"
            confirmation
        >
            <p>Are you sure you want to cancel this mid-term adjustment?</p>
        </modal>

        <modal
            v-if="isInsurer"
            ref="midTermAdjustmentConfirmation"
            title="Mid-term premium adjustment"
            confirmation
            :action-text="reviewAction === 'accept' ? 'Accept' : 'Reject'"
        >
            <p>
                You are about to {{ reviewAction }} mid-term premium adjustment for risk {{ declaration.risk_id }}
            </p>
        </modal>

        <x-dialog
            v-if="isInsurer && showMidTermAdjustmentDialog"
            v-model="showMidTermAdjustmentDialog"
            width="800"
            height="600"
        >
            <x-container fluid>
                <x-layout>
                    <x-flex xs12>
                        <h2>Mid-term adjustment</h2>
                    </x-flex>
                </x-layout>

                <x-layout mt-3>
                    <x-flex xs12>
                        <data-list v-model="adjustmentDateList"/>
                    </x-flex>
                </x-layout>

                <x-layout mt-3>
                    <x-flex xs12>
                        <h4>Details</h4>
                        <data-list v-model="adjustmentDataList"/>
                    </x-flex>
                </x-layout>

                <x-layout mt-3>
                    <x-flex xs12>
                        <h4>Commentary</h4>

                        <template v-if="myLineAdjustmentRows.length > 1">
                            <div
                                v-for="row in myLineAdjustmentRows"
                                :key="row.line_id"
                                class="text-editor-output"
                            >
                                <strong>{{ row.label }}:</strong> {{ row.commentary }}
                            </div>
                        </template>

                        <div v-else class="text-editor-output">{{ endorsementRequest.commentary }}</div>
                    </x-flex>
                </x-layout>

                <x-layout mt-3>
                    <x-flex xs12>
                        <h4>Documents</h4>
                    </x-flex>
                </x-layout>

                <x-layout mt-3>
                    <x-flex>
                        <document-list
                            class="mt-3"
                            :upload="false"
                            :can-delete="() => false"
                            :facility-id="declaration.facility_id"
                            :documentable-id="endorsementRequest.endorsement_id"
                            documentable-type="endorsement"
                            @documentsLoading="documentsLoading = $event"
                        />
                    </x-flex>
                </x-layout>

                <x-layout class="response-actions" justify-space-between style="align-items: baseline">
                    <x-flex xs4 class="text-xs-left">
                        <x-btn
                            color="tertiary"
                            small
                            :loading="midTermAdjustmentLoading || documentsLoading"
                            @click="showMidTermAdjustmentDialog=false"
                            data-test="cancel"
                        >
                            Cancel
                        </x-btn>
                    </x-flex>


                    <x-flex v-if="endorsementRequest.status === 'not_required'" xs8 class="text-xs-right">
                        <p>Your approval is not required.</p>
                    </x-flex>

                    <x-flex v-else xs8 class="text-xs-right">
                        <x-btn
                            color="tertiary"
                            small
                            :loading="midTermAdjustmentLoading || documentsLoading"
                            @click="submitMidTermAdjustmentConfirmation('reject')"
                        >
                            Decline MTA
                        </x-btn>

                        <x-btn
                            class="ml-2"
                            color="primary"
                            small
                            :loading="midTermAdjustmentLoading || documentsLoading"
                            @click="submitMidTermAdjustmentConfirmation('accept')"
                        >
                            Accept MTA
                        </x-btn>
                    </x-flex>
                </x-layout>
            </x-container>
        </x-dialog>
    </div>
</template>

<script>
import StatusChip from "~/components/Shared/UIComponents/StatusChip";
import Modal from "~/components/Shared/UIComponents/Modal";
import ActionIcon from "~/components/Shared/UIComponents/ActionIcon";
import DataList from '~/components/Shared/UIComponents/DataList';
import DocumentList from "~/components/Shared/UIComponents/DocumentList";
import rules from "~/mixins/rules";

export default {
    name: "MidTermAdjustmentTable",
    props: {
        midTermAdjustment: {
            type: Array,
            default: [],
        },
        declaration: {
            type: Object,
            default: {},
        },
        userType: {
            type: String,
            default: '',
        },
        mtaLoading: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        StatusChip,
        Modal,
        ActionIcon,
        DataList,
        DocumentList
    },
    mixins: [rules],
    data() {
        return {
            mtaMainHeaders: [
                {
                    text: "Effective Date",
                    value: "effective_date",
                    width: "10%"
                },
                {
                    text: "Reference",
                    value: "reference",
                    width: "5%"
                },
                {
                    text: "Title",
                    value: "title",
                    sortable: false,
                    width: "20%"
                },
                {
                    text: "Total MTA",
                    value: "adjustment_amount",
                    width: "10%"
                },
                {
                    text: "Settlement Date",
                    value: "settlement_due_date",
                    width: "10%"
                },
                {
                    text: "Premium payment term",
                    value: "premium_payment_term",
                    sortable: false,
                    width: "10%"
                },
                {
                    text: "Status",
                    sortable: false,
                    width: "10%",
                },
                {
                    text: "actions",
                    sortable: false,
                    align: "right",
                    width: "20%",
                },
                {
                    text: "",
                    sortable: false,
                    width: "5%",
                },
            ],
            loading: false,
            showMidTermAdjustmentDialog: false,
            midTermAdjustmentLoading: false,
            reviewAction: null,
            disabled: false,
            documentsLoading: false,
            expand: false,
            editingMTA: null,
            updatingEditingMTA: false,
            dialog: {},
        };
    },
    created() {
        this.$eventBus.$on('action-review-mid-term-adjustment', () => {
            this.reviewMidTermAdjustment()
        });
    },
    methods: {
        canEditMidTermAdjustment(status) {
            return this.isBroker && status === "pending";
        },
        canEditMidTermAdjustmentApproval(approvalStatus, mtaStatus) {
            return this.isBroker &&
                ["pending", "draft"].includes(mtaStatus) &&
                ["pending", "not_required"].includes(approvalStatus);
        },
        canCancel(status) {
            return this.isBroker && ['pending', 'draft'].includes(status);
        },
        canPreview(midTermAdjustment) {
            const endorsementRequest = midTermAdjustment.endorsement_requests.find(request => request.insurer_company_id === this.$root.user.company_id);
            return this.isInsurer &&
                midTermAdjustment.status === "pending" &&
                ["pending", "not_required"].includes(endorsementRequest?.status);
        },
        canDownloadMTA(midTermAdjustment) {
            return midTermAdjustment.status === 'approved' || midTermAdjustment.status === 'applied';
        },
        canOpenMta(midTermAdjustment) {
            return this.isBroker && midTermAdjustment.status === 'draft';

        },
        goToMta() {
            this.$router.push({path: `/agent/risk/${this.$route.params.id}/mid-term-adjustment`});
        },
        editMidTermAdjustment(item) {
            this.$router.push({path: `/agent/risk/${this.$route.params.id}/mid-term-adjustment/${item.id}`});
        },
        getInsurerCompanyName(endorsement_request) {
            if (endorsement_request.captive_insurer_company) {
                return endorsement_request.captive_insurer_company.name;
            }

            return endorsement_request.insurer_company.name;
        },
        getTotalMTA(adjustment) {
            return adjustment.endorsement_requests.reduce((totalMTA, adjustmentRequest) => {
                if (adjustmentRequest.adjustment_amount === null) {
                    return totalMTA;
                }

                const adjustmentValue = this.isInsurer && !this.isInsurerUser(adjustmentRequest.insurer_company_id) ? 0 : parseFloat(adjustmentRequest.adjustment_amount);

                return totalMTA + adjustmentValue;
            }, 0);
        },
        getInsurerLine(insurerCompanyId) {
            return this.declaration.lines.find(line => line.insurer_company_id === insurerCompanyId);
        },
        adjustmentRows(midTermAdjustment) {
            const rows = [];

            midTermAdjustment.endorsement_requests.forEach((request) => {
                const insurerLines = this.declaration.lines.filter(line => (line.captive_insurer_company_id || line.insurer_company_id) === request.insurer_company_id);
                const adjustments = midTermAdjustment.line_adjustments || [];

                const lineRows = insurerLines
                    .map(line => ({
                        line,
                        adjustment: adjustments.find(lineAdjustment => lineAdjustment.line_id === line.id)
                    }))
                    .filter(({adjustment}) => Boolean(adjustment));

                if (lineRows.length === 0) {
                    rows.push({
                        row_key: `${request.id}-aggregate`,
                        request,
                        line: insurerLines[0] || null,
                        adjustment_amount: request.adjustment_amount,
                        commentary: request.commentary,
                        editable: insurerLines.length <= 1,
                        per_line: false,
                    });
                    return;
                }

                lineRows.forEach(({line, adjustment}) => {
                    rows.push({
                        row_key: `${request.id}-${line.id}`,
                        request,
                        line,
                        adjustment_amount: adjustment.adjustment_amount,
                        commentary: adjustment.commentary,
                        editable: true,
                        per_line: true,
                    });
                });
            });

            return rows;
        },
        editInsurerMTA(row) {
            this.editingMTA = {
                row_key: row.row_key,
                endorsement_id: row.request.endorsement_id,
                approval_id: row.request.id,
                line_id: row.line ? row.line.id : null,
                per_line: row.per_line,
                adjustment_amount: row.adjustment_amount,
                commentary: row.commentary,
            };
        },
        isEditing(row) {
            return this.editingMTA && this.editingMTA.row_key === row.row_key;
        },
        async updateInsurerMTA() {
            if (!this.editingMTA) {
                return;
            }

            this.updatingEditingMTA = true;

            const url = this.editingMTA.per_line
                ? `broker/declaration/${this.declaration.id}/mid-term-adjustment/${this.editingMTA.endorsement_id}/line/${this.editingMTA.line_id}/adjustment`
                : `broker/declaration/${this.declaration.id}/mid-term-adjustment/${this.editingMTA.endorsement_id}/approval/${this.editingMTA.approval_id}`;

            axios
                .put(
                    this.$root.api(url),
                    {
                        'adjustment_amount': this.editingMTA.adjustment_amount,
                        'commentary': this.editingMTA.commentary
                    }
                )
                .then(() => {
                    this.editingMTA = null;
                    this.updatingEditingMTA = false;
                    this.$emit("loadMidTermAdjustment");
                })
                .catch((error) => {
                    this.updatingEditingMTA = false;
                });
        },
        async cancelMidTermAdjustment(midTermAdjustment) {
            if (
                (await this.$refs.confirmMidTermAdjustmentCancel.open()) ===
                false
            ) {
                return;
            }
            this.disabled = true;
            axios
                .put(
                    this.$root.api(
                        this.cancelUrl(midTermAdjustment)
                    )
                )
                .then(() => {
                    this.disabled = false;
                    this.$emit("updateDeclarationData");
                })
                .catch((error) => {
                    this.disabled = false;
                });
        },
        async downloadMTAMarketSheet(midTermAdjustment) {
            this.disabled = true;

            const fileUrl = (await axios.get(`/api/declaration/${this.$route.params.id}/mid-term-adjustment/${midTermAdjustment.id}/download`)).data.url;
            window.location.href = fileUrl;

            this.disabled = false;

            this.status = 'idle';
        },
        reviewMidTermAdjustment() {
            this.showMidTermAdjustmentDialog = true;
        },
        async submitMidTermAdjustmentConfirmation(reviewAction) {
            this.reviewAction = reviewAction;

            if (!this.isInsurer) {
                return;
            }

            if ((await this.$refs.midTermAdjustmentConfirmation.open()) === false) {
                return;
            }

            this.midTermAdjustmentLoading = true;

            this.loading = false;

            const endPoint = this.reviewAction === 'reject' ? 'decline' : this.reviewAction;

            await axios.put(this.$root.api(`insurer/endorsement/${this.endorsementRequest.endorsement_id}/${endPoint}`)).then(() => {
                this.showMidTermAdjustmentDialog = false;
                this.midTermAdjustmentLoading = false;
                this.$router.push({name: 'insurer-dashboard'});
            }).catch(error => {
                this.midTermAdjustmentLoading = false;
            });
        },
        getStatus(status, insurerCompanyId = null) {
            return status === 'captive_reviewing' ? 'pending' : status;
        },
        isInsurerUser(insurerCompanyId) {
            return insurerCompanyId === this.$root.user.company_id;
        },
        showViewDocumentsModal(endorsement) {
            return ['approved', 'applied'].includes(endorsement.status);
        },
        cancelUrl(midTermAdjustment) {
            let cancelPendingUrl =  `broker/declaration/${this.declaration.id}/mid-term-adjustment/${midTermAdjustment.id}/cancel`;
            let cancelDraftUrl =  `broker/declaration/${this.declaration.id}/mid-term-adjustment/${midTermAdjustment.id}/cancel-draft`;

            return this.isDraft(midTermAdjustment) ? cancelDraftUrl : cancelPendingUrl;
        },
        isDraft(midTermAdjustment) {
            return midTermAdjustment.status === 'draft';
        }
    },
    computed: {
        midTermAdjustmentFormatted() {
            this.midTermAdjustment.forEach((adjustment) => {
                adjustment.endorsement_requests.forEach((adjustmentRequest) => {
                    const insurerLine = this.declaration.lines.find(line => line.insurer_company_id === adjustmentRequest.insurer_company_id);
                    adjustmentRequest.line = insurerLine;
                });
            });

            return this.midTermAdjustment;
        },
        isCaptiveReinsurance() {
            return this.declaration.transaction_type === 'captive_reinsurance';
        },
        isInsurer() {
            return this.userType === 'insurer';
        },
        isBroker() {
            return this.userType === 'broker';
        },
        endorsementRequest() {
            const endorsement = this.midTermAdjustment.find(adjustment => adjustment.status === 'pending');
            const endorsementRequest = endorsement.endorsement_requests.find(request => request.insurer_company_id === this.$root.user.company_id);

            endorsementRequest.endorsement = endorsement;

            return endorsementRequest;
        },
        adjustmentDateList() {
            return [
                {
                    text: 'Effective date',
                    value: this.$moment(this.endorsementRequest.endorsement.effective_date).format("DD/MM/YYYY"),
                },
                {
                    text: 'Settlement due date',
                    value: this.$moment(this.endorsementRequest.endorsement.settlement_due_date).format("DD/MM/YYYY"),
                },
            ];
        },
        adjustmentDataList() {
            const premiumPaymentTerm = this.declaration.facility ? this.declaration.facility.premium_payment_term : 60;

            const adjustmentEntries = this.myLineAdjustmentRows.length > 1
                ? [
                    ...this.myLineAdjustmentRows.map(row => ({
                        text: `Mid-term adjustment — ${row.label}`,
                        value: this.$options.filters.currency(row.adjustment_amount, this.declaration.currency.code),
                    })),
                    {
                        text: 'Total mid-term adjustment',
                        value: this.$options.filters.currency(this.endorsementRequest.adjustment_amount, this.declaration.currency.code),
                    },
                ]
                : [
                    {
                        text: 'Mid-term adjustment',
                        value: this.$options.filters.currency(this.endorsementRequest.adjustment_amount, this.declaration.currency.code),
                    },
                ];

            return [
                {
                    text: 'Title',
                    value: this.endorsementRequest.endorsement.title,
                },
                {
                    text: 'Written line',
                    value: this.$options.filters.currency(this.isCaptiveReinsurance ? this.declaration.current_contract_value : this.declaration.limit_of_liability, this.declaration.currency.code),
                },
                ...adjustmentEntries,
                {
                    text: 'Premium payment term',
                    value: premiumPaymentTerm + ' days',
                }
            ];
        },
        myLineAdjustmentRows() {
            const adjustments = this.endorsementRequest?.endorsement?.line_adjustments || [];

            if (adjustments.length === 0) {
                return [];
            }

            const myLines = (this.declaration.lines || []).filter(line => [
                line.insurer_company_id,
                line.captive_insurer_company_id,
            ].includes(this.$root.user.company_id));

            return myLines
                .map(line => {
                    const adjustment = adjustments.find(lineAdjustment => lineAdjustment.line_id === line.id);

                    if (!adjustment) {
                        return null;
                    }

                    return {
                        line_id: line.id,
                        label: line.security ? line.security.name : 'No security',
                        adjustment_amount: adjustment.adjustment_amount,
                        commentary: adjustment.commentary,
                    };
                })
                .filter(Boolean);
        },
        mtaSubHeaders() {
            const mtaSubHeaders = [
                {
                    text: "Insurer/Security",
                    value: "insurer",
                    sortable: false,
                    width: "30%",
                    class: "sub-header"
                }
            ];

            if (!this.isInsurer) {
                mtaSubHeaders.push(
                    {
                        text: "Written line",
                        value: "line_amount",
                        sortable: false,
                        width: "15%",
                        class: "sub-header",
                        align: "right",
                    }
                );
            }

            mtaSubHeaders.push(
                {
                    text: "Adjustment",
                    value: "adjustment_amount",
                    sortable: false,
                    width: "15%",
                    class: "sub-header"
                },
                {
                    text: "Commentary",
                    value: "commentary",
                    sortable: false,
                    width: "30%",
                    class: "sub-header"
                },
                {
                    text: "Status",
                    sortable: false,
                    width: "15%",
                    class: "sub-header"
                }
            );

            return mtaSubHeaders;
        },
    },

    beforeDestroy() {
        this.$eventBus.$off('action-review-mid-term-adjustment');
    }
};
</script>
<style lang="scss" scoped>
.mid-term-adjustment-request-approval {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .mid-term-adjustment-request-approval {
        margin-top: 0.5em;
    }

    .x-tag {
        margin: 0 0 0 1em;
    }
}

.btn-pointer :hover {
    cursor: pointer;
}

.underwriter-details {
    font-size: 12px;
}

:deep() {
    v-table.x-datatable tbody tr td.vertical-align-top {
        vertical-align: top;
    }
}
</style>

